import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, isAdminScope } from "@/lib/api";
import { useIdleLogout, markActive } from "@/lib/idle";

const sessionKey = () => (isAdminScope() ? "hasAdminSession" : "hasSession");

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      localStorage.setItem(sessionKey(), "1");
    } catch (e) {
      setUser(null);
      localStorage.removeItem(sessionKey());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem(sessionKey())) {
      checkAuth();
    } else {
      setLoading(false);
    }
    const expired = () => { setUser(null); localStorage.removeItem(sessionKey()); };
    const onStorage = (e) => {
      if (e.key !== sessionKey()) return;
      if (e.newValue) checkAuth(); else setUser(null);
    };
    window.addEventListener("auth:expired", expired);
    window.addEventListener("storage", onStorage);
    return () => { window.removeEventListener("auth:expired", expired); window.removeEventListener("storage", onStorage); };
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    markActive(sessionKey());
    setUser(res.data);
    localStorage.setItem(sessionKey(), "1");
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await api.post("/auth/register", { name, email, password });
    markActive(sessionKey());
    setUser(res.data);
    localStorage.setItem(sessionKey(), "1");
    return res.data;
  };

  const googleLogin = async (code) => {
    const res = await api.post("/auth/google", { code });
    markActive(sessionKey());
    setUser(res.data);
    localStorage.setItem(sessionKey(), "1");
    return res.data;
  };

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem(sessionKey());
    localStorage.removeItem(`${sessionKey()}_lastActive`);
    setUser(null);
  }, []);

  const onIdle = useCallback(() => {
    logout();
    toast.info("You were signed out after 45 minutes of inactivity. Please sign in again.", { id: "idle-logout" });
  }, [logout]);
  useIdleLogout(user, sessionKey(), onIdle);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleLogin, logout, setUser, checkAuth, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
